import { defineStore } from "pinia";
import { MarketplaceApi } from "~/api/marketplace";
import { IAuctionItem, IMarketplaceFilter } from "~/common/interfaces/IMarketplace";
import { BigNumber, ethers } from "ethers";
import { secondaryExecutorConfig } from "~/generated";
import useConvert from "~/composables/useConvert";
import i18n from "~/modules/i18n";
import { useStoreUserV2 } from "./storeUserV2";
import { useStoreApp, NotifType } from "./storeApp";
import { AuctionUpdate, Currency, MarketType, NftSkill, Pagination } from "fungi-types";
import { useTracking } from "~/composables/useGtm";
import { MarketPlaceFilter } from "~/api/auction/types";
import { auctionApi } from "~/api/auction";
import { SECONDARY_EXECUTOR_ADDRESS, useWeb3AuthStore } from "./storeWeb3auth";
import { Account, Address, encodeFunctionData, formatEther } from "viem";
import { polygon } from "viem/chains";
import { usdcContractMainnet } from "~/application/config";
import {
  createMeeClient,
  toMultichainNexusAccount,
  getMEEVersion,
  MEEVersion,
} from "@biconomy/abstractjs";
import { ERC20_ABI } from "fungi-utils/abis";
import { WalletClient } from "viem";
import { erc20Abi } from "viem";
import { USDC_TOKEN_ADDRESS } from "~/application/config";

const { weiToMatic, maticToWei, usdcToUsd, usdToUsdc } = useConvert;

export const useSecondaryMarketplace = defineStore("secondaryMarketplace", {
  state: () => ({
    api: new MarketplaceApi(MarketType.SECONDARY),
    auctions: [] as Array<NftSkill>,
    stickyAuctions: [] as Array<NftSkill>,
    privateAuctions: [] as Array<NftSkill>,
    pagination: {
      page: 1,
      limit: 20,
    } as Pagination,
    filters: {} as Partial<MarketPlaceFilter>,
    loading: false,
  }),

  getters: {
    activeFilters(state) {
      return Object.values(state.filters).reduce((count: number, value) => {
        if (
          ((typeof value === "string" || Array.isArray(value)) && value.length > 0) ||
          (typeof value === "boolean" && value === true)
        ) {
          return count + 1;
        }
        return count;
      }, 0);
    },
  },

  actions: {
    async getCards() {
      try {
        const auctions = await auctionApi.auctions.secondary.list.query({
          page: 1,
          ...this.filters,
        });
        this.auctions = auctions.result as NftSkill[];
        this.pagination = auctions.pagination;
        this.loading = false;
      } catch (e) {
        console.log("e", e);
      }
    },

    async updateFilters(filters: Partial<MarketPlaceFilter>) {
      const storeUser = useStoreUserV2();
      const migratedToUsdc = storeUser.migrationToUSDCCompleted;
      this.filters = { ...filters, currency: migratedToUsdc ? Currency.USDC : undefined };
      if (filters.price?.[0] && filters.price?.[1]) {
        filters.price[0] = usdToUsdc(+filters.price[0]).toString();
        filters.price[1] = usdToUsdc(+filters.price[1]).toString();
      }
      await this.getCards();
    },

    async nextPage() {
      this.loading = true;
      if (this.pagination.hasNextPage) {
        const response = await auctionApi.auctions.secondary.list.query({
          page: this.pagination.page + 1,
          limit: this.pagination.limit,
          ...this.filters,
        });
        this.auctions.push(...(response.result as NftSkill[]));
        this.pagination = response.pagination;
      }
      this.loading = false;
    },

    async resetFilters() {
      this.filters = {} as MarketPlaceFilter;
      await this.getCards();
    },

    updateAuction(data: AuctionUpdate) {
      this.$patch((state) => {
        const auctionIdx = state.auctions.findIndex(
          (auction) => auction.auction?.cuid === data.auction.cuid
        );

        if (auctionIdx === -1) return;

        state.auctions[auctionIdx].status = data.auction.status as any;
      });
    },

    removeCard(cuid: string) {
      this.$patch((state) => {
        const index = state.auctions.findIndex((item) => item.auction?.cuid === cuid);
        if (index !== -1) {
          state.auctions.splice(index, 1);
        }
      });
    },

    async buyItem_old(item: NftSkill) {
      const storeApp = useStoreApp();
      const web3authStore = useWeb3AuthStore();
      const { t } = i18n.global;
      if (!item.auction) throw new Error("Item does not have an auction");

      // 1. Check wallet is connected
      if (
        !web3authStore.isConnected ||
        !web3authStore.userWalletClient ||
        !web3authStore.polygonReader ||
        !web3authStore.signer // signer needed for meeClient
      ) {
        throw new Error("Wallet not connected");
      }

      let currency = item.auction.currency === Currency.USDC ? Currency.USDC : Currency.WPOL;
      let amount = weiToMatic(item.auction.startedAmount);
      if (currency === Currency.USDC) {
        const usdAmount = usdcToUsd(BigNumber.from(item.auction.startUsdcAmount));
        amount = usdAmount;
      }

      const approval = await web3authStore.ensureAllowance({
        market: MarketType.SECONDARY,
        currency: currency,
        requiredAmount: amount,
      });

      if (!approval.success) {
        throw new Error("Failed to approve WPOL");
      }

      const buyParams = await this.api.buy(item.auction.cuid);

      // 3. Estimate gas fee
      console.log("Estimating gas...");
      const gasAmount = await web3authStore.estimateContractGas({
        address: SECONDARY_EXECUTOR_ADDRESS,
        abi: secondaryExecutorConfig.abi,
        functionName: "buy",
        args: [buyParams],
      });

      // 4. Calculate gas fee with 200% buffer
      const gasFee = await web3authStore.calculateGasFee(gasAmount, 110);
      console.log(`Gas fee: ${formatEther(gasFee)} POL`);

      // 5. Check if enough POL for gas
      const hasGas = await web3authStore.hasEnoughGas(gasFee);

      if (!hasGas) {
        storeApp.displayNotification(
          NotifType.BUY_FAILED,
          item,
          t("pages.marketplace.trade.errors.insufficient-matic-balance")
        );
        return;
      }

      // 6. Get optimal gas settings
      const feeData = await web3authStore.getGasPrice();

      // 7. Execute transaction
      console.log("Executing purchase...");
      const hash = await web3authStore.userWalletClient.writeContract({
        address: SECONDARY_EXECUTOR_ADDRESS,
        abi: secondaryExecutorConfig.abi,
        functionName: "buy",
        args: [buyParams],
        account: web3authStore.walletAddress as Address,
        chain: polygon,
        maxFeePerGas: feeData.maxFeePerGas,
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
        gas: gasAmount,
      });

      // 8. Wait for confirmation
      const receipt = await web3authStore.polygonReader.waitForTransactionReceipt({ hash });

      console.log("Purchase successful!", receipt.transactionHash);

      await web3authStore.getWpolBalance();

      storeApp.displayNotification(NotifType.BUY_SUCCESS, item);
    },

    async buyItem_new(item: NftSkill) {
      const storeApp = useStoreApp();
      const web3authStore = useWeb3AuthStore();
      if (!item.auction) throw new Error("Item does not have an auction");

      // 1. Check wallet is connected
      if (!web3authStore.isConnected || !web3authStore.smartWalletClient) {
        throw new Error("Wallet not connected");
      }

      // 2. Make sure USDC is used
      let currency = item.auction.currency === Currency.USDC ? Currency.USDC : Currency.WPOL;
      if (currency !== Currency.USDC) {
        throw new Error("Only USDC purchases are supported in the new implementation");
      }

      // 3. Get buy params from API
      const buyParams = await this.api.buy(item.auction.cuid);

      // 4. SEND the UserOperation to the Smart Wallet
      // Approve + Buy in a single UserOperation
      const res = await web3authStore.smartWalletClient.sendUserOperation({
        uo: [
          {
            target: USDC_TOKEN_ADDRESS,
            data: encodeFunctionData({
              abi: erc20Abi, // ABI standard ERC20
              functionName: "approve",
              // Approve SecondaryExecutor to pull USDC with the exact amount
              args: [SECONDARY_EXECUTOR_ADDRESS, BigInt(item.auction.startUsdcAmount)],
            }),
          },
          {
            target: SECONDARY_EXECUTOR_ADDRESS,
            data: encodeFunctionData({
              abi: secondaryExecutorConfig.abi, // SecondaryExecutor ABI
              functionName: "buy", // buy function
              args: [buyParams], // buy params from API
            }),
          },
        ],
      });

      console.log("UserOp Hash envoyé:", res.hash);

      // 5. Wait for transaction confirmation
      const txHash = await web3authStore.smartWalletClient.waitForUserOperationTransaction({
        hash: res.hash,
      });

      // 6. Notify success
      await web3authStore.getWpolBalance();
      storeApp.displayNotification(NotifType.BUY_SUCCESS, item);
    },
  },
});
